import { useEffect, useState } from 'react';

interface Props {
  quantity: number;
  onQuantityChange: (next: number) => void;
  label?: string;
  // May contain <br .../> tags (from i18n) — rendered as responsive line
  // breaks; all other content is rendered as plain text.
  errorMessage: string;
}

const MIN_QTY = 1;
const MAX_QTY = 999;

// Validated quantity stepper: minus / text input / plus. Invalid input is
// blocked — the field keeps its last valid value and the error shows.
export const QuantityStepper = ({ quantity, onQuantityChange, label, errorMessage }: Props) => {
  const [rawQty, setRawQty] = useState(String(quantity));
  const [qtyError, setQtyError] = useState(false);

  const setQuantity = (next: number) => {
    const clamped = Math.max(MIN_QTY, Math.min(MAX_QTY, next));
    setRawQty(String(clamped));
    setQtyError(false);
    onQuantityChange(clamped);
  };

  useEffect(() => {
    setRawQty(String(quantity));
  }, [quantity]);

  const onQtyInput = (v: string) => {
    // Empty is a transient, error-free state so the value can be cleared and
    // retyped; nothing is committed until a valid quantity is entered.
    if (v === '') {
      setRawQty('');
      setQtyError(false);
      return;
    }
    if (/^[1-9]\d{0,2}$/.test(v)) {
      setRawQty(v);
      setQtyError(false);
      setQuantity(Number(v));
    } else {
      setQtyError(true);
    }
  };

  // Leaving the field empty restores the last valid quantity and flags it.
  const onBlur = () => {
    if (rawQty === '') {
      setRawQty(String(quantity));
      setQtyError(true);
    }
  };

  return (
    <div className='inline-block align-middle whitespace-nowrap'>
      {label && <p className='text-center text-primary-dark'>{label}</p>}
      <div
        className={`relative flex flex-nowrap justify-center items-center font-secondary rounded-md text-lg border-2 leading-10 bg-gray-100 text-center text-primary-dark ${
          qtyError ? 'border-red-500' : 'border-primary-dark'
        }`}
      >
        <button
          type='button'
          aria-label='Decrease quantity by one'
          className='group px-2 py-3 hover:cursor-pointer disabled:opacity-40'
          onClick={(e) => {
            e.stopPropagation();
            setQuantity(quantity - 1);
          }}
        >
          <img
            alt='minus sign'
            className='min-w-[12px] group-active:scale-75'
            src='https://assets.share-a-cart.com/images/next/simple-minus.svg'
          />
        </button>
        <input
          aria-label='item quantity'
          className='item-quantity-input w-10 border-none text-center bg-white focus:ring-0'
          type='text'
          inputMode='numeric'
          value={rawQty}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onQtyInput(e.target.value)}
          onBlur={onBlur}
        />
        <button
          type='button'
          aria-label='Increase quantity by one'
          className='group px-2 py-3 hover:cursor-pointer disabled:opacity-40'
          onClick={(e) => {
            e.stopPropagation();
            setQuantity(quantity + 1);
          }}
        >
          <img
            alt='plus sign'
            className='min-w-[12px] group-active:scale-75'
            src='https://assets.share-a-cart.com/images/next/simple-plus.svg'
          />
        </button>
        {qtyError && (
          <p className='absolute left-1/2 top-full z-10 mt-1 w-max -translate-x-1/2 text-center text-sm text-red-500'>
            {errorMessage.split(/<br[^>]*\/?>/).map((part, i) => (
              <span key={i}>
                {i > 0 && <br className='lg:hidden' />}
                {part}
              </span>
            ))}
          </p>
        )}
      </div>
      {/* Balances the label's height so the stepper box (not the label+box
          block) vertically centers against siblings like the price; grows
          during the error state to reserve room for the overlaid message. */}
      {label && <div aria-hidden className={qtyError ? 'h-12 lg:h-7' : 'h-6 sm:hidden'} />}
    </div>
  );
};
